import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, setAuthToken, getAuthToken, refreshSession, isRefreshRace, LEGACY_PERSIST_KEY } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const RESTORE_BACKOFF_MS = [1000, 2500, 5000];   // rides out a backend restart / new replica instead of declaring the owner signed out

// Silent restore from the HttpOnly persistent owner cookie (Iter126A). Only a definitive 401/403 means "no persistent login";
// a transient failure (timeout / 5xx / network — e.g. the backend is restarting) is retried with backoff so a valid trusted-device
// credential never turns into a redirect to /login. A 409 means a concurrent refresh already won → the Bearer is in localStorage.
const restoreSession = async () => {
  for (let i = 0; ; i++) {
    try { await refreshSession(); return true; }
    catch (e) {
      const st = e?.response?.status;
      if (isRefreshRace(e)) return true;
      if (st === 401 || st === 403) return false;
      if (i >= RESTORE_BACKOFF_MS.length) throw e;
      await sleep(RESTORE_BACKOFF_MS[i]);
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const checkAuth = useCallback(async () => {
    // /auth/me is called with the interceptor's silent-refresh disabled: the restore sequence below owns that decision.
    const fetchMe = () => api.get("/auth/me", { _bfRetried: true });
    try {
      let res;
      try { res = await fetchMe(); }
      catch (e) {
        const st = e?.response?.status;
        if (st === 401) {
          // Short session missing/expired → restore from the persistent credential (rotated server-side), then retry /me once.
          const restored = await restoreSession();
          if (!restored) { setAuthToken(null); throw e; }
          res = await fetchMe();
        } else if (st === 403) throw e;
        else res = await fetchMe();            // transient (timeout / 5xx / network): retry once before giving up
      }
      setUser(res.data);
      if (!res.data.is_admin && !res.data.profile_complete) {
        setPersonaOpen(true);
      }
      return res.data;
    } catch (e) {
      const st = e?.response?.status;
      // Only a real auth rejection signs the user out; a flaky network never should
      if (st === 401 || st === 403 || !getAuthToken()) setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try { localStorage.removeItem(LEGACY_PERSIST_KEY); } catch (e) {}   // the persistent credential never lives in JS-readable storage
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const openAuth = (mode = "login") => { setAuthMode(mode); setAuthOpen(true); };
  const closeAuth = () => setAuthOpen(false);

  // Native email/password auth: open the sign-in dialog (Emergent Google OAuth is temporarily disabled).
  const login = () => openAuth("login");

  // Log out = AWAIT server revocation (session + HttpOnly persistent owner cookie) BEFORE claiming success. If the server
  // cannot be reached, nothing is cleared and the user is told — a silent local "logout" would leave a valid persistent cookie.
  const logout = async () => {
    const t = getAuthToken();
    try {
      const r = await api.post("/auth/logout", {}, { headers: t ? { Authorization: `Bearer ${t}` } : {}, timeout: 15000 });
      if (!r?.data?.ok) throw new Error("logout not confirmed");
    } catch (e) {
      toast.error("Could not sign out — the server did not confirm. Check your connection and try again.");
      return false;
    }
    setUser(null);
    setAuthToken(null);
    try { localStorage.removeItem(LEGACY_PERSIST_KEY); } catch (e) {}
    window.location.href = "/";
    return true;
  };

  // Returns true if user can act (vote/submit); otherwise shows the free-join gate or persona setup.
  const requireAction = () => {
    if (!user) { setJoinOpen(true); return false; }
    if (!user.is_admin && !user.profile_complete) { setPersonaOpen(true); return false; }
    return true;
  };

  const openPersona = () => setPersonaOpen(true);
  const closePersona = () => setPersonaOpen(false);
  const closeJoin = () => setJoinOpen(false);

  const canUpload = !!user && (user.is_admin || (user.gender || "").toLowerCase() === "female");

  // Gate for uploading: must be signed in + persona complete + (woman or owner)
  const requireUpload = () => {
    if (!requireAction()) return false;
    if (!(user.is_admin || (user.gender || "").toLowerCase() === "female")) {
      toast.error("Only women can upload on BareFootage — you can still view, rate and comment.");
      return false;
    }
    return true;
  };

  const onPersonaSaved = (updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
    setPersonaOpen(false);
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, login, logout, checkAuth,
      personaOpen, openPersona, closePersona, requireAction, onPersonaSaved,
      canUpload, requireUpload,
      joinOpen, closeJoin,
      authOpen, authMode, openAuth, closeAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
